import React, { useState } from 'react';
import { X, Share2, Mail, Users, Send, Search, Plus, Shield, Eye, Edit, Crown, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getUnitTypeName } from '../../data/mockData';
import { supabase } from '../../lib/supabase';

interface ShareCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendar: any;
  onSendInvitation: (invitationData: any) => void;
}

const ShareCalendarModal: React.FC<ShareCalendarModalProps> = ({
  isOpen,
  onClose,
  calendar,
  onSendInvitation
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    userId: '',
    permissionLevel: 'viewer' as 'viewer' | 'editor' | 'admin',
    message: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  React.useEffect(() => {
    if (isOpen && user?.branchId) {
      fetchUsers();
    }
  }, [isOpen, user?.branchId]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      // Fetch users from the same branch, excluding the current user
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email, avatar, role, unit_type') // Added unit_type
        .eq('branch_id', user?.branchId)
        .neq('id', user?.id)
        .eq('is_active', true)
        .order('unit_type', { ascending: true }) // Order by unit type first
        .order('name');

      if (error) throw error;
      setAvailableUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Group users by unit type
  const groupedUsers = React.useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    availableUsers.forEach(user => {
      const type = user.unit_type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(user);
    });
    return groups;
  }, [availableUsers]);

  const permissionLevels = [
    {
      value: 'viewer',
      label: 'Görüntüleyici',
      description: 'Sadece etkinlikleri görüntüleyebilir',
      icon: Eye,
      color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400'
    },
    {
      value: 'editor',
      label: 'Düzenleyici',
      description: 'Etkinlik oluşturabilir ve düzenleyebilir',
      icon: Edit,
      color: 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400'
    },
    {
      value: 'admin',
      label: 'Yönetici',
      description: 'Tüm yetkilere sahip, başkalarını davet edebilir',
      icon: Crown,
      color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400'
    }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    try {
      await onSendInvitation(formData);

      // Reset form
      setFormData({
        userId: '',
        permissionLevel: 'viewer',
        message: ''
      });
    } catch (error) {
      console.error('Error sending invitation:', error);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Takvim Paylaş</h2>
              <p className="text-gray-600 dark:text-gray-400">{calendar?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Calendar Info */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: calendar?.color }}
              />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">{calendar?.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {calendar?.description || 'Açıklama yok'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Tür: {calendar?.type === 'personal' ? 'Kişisel' :
                    calendar?.type === 'shared' ? 'Paylaşımlı' : 'Takım'} Takvim
                </p>
              </div>
            </div>
          </div>

          {/* Invitation Form */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Kullanıcı Davet Et</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kullanıcı Seç <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />


                  // ...
                  <select
                    name="userId"
                    value={formData.userId}
                    onChange={handleChange}
                    required
                    disabled={isLoadingUsers}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  >
                    <option value="">
                      {isLoadingUsers ? 'Kullanıcılar yükleniyor...' : 'Bir kullanıcı seçin'}
                    </option>

                    {Object.entries(groupedUsers).map(([type, users]) => (
                      <optgroup key={type} label={getUnitTypeName(type as any)}>
                        {users.map((u: any) => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.role === 'admin' ? 'Yönetici' : u.role})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
                {availableUsers.length === 0 && !isLoadingUsers && (
                  <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
                    Aynı şubede paylaşım yapılabilecek başka kullanıcı bulunamadı.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Yetki Seviyesi
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {permissionLevels.map((level) => {
                    const Icon = level.icon;
                    return (
                      <label
                        key={level.value}
                        className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.permissionLevel === level.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                      >
                        <input
                          type="radio"
                          name="permissionLevel"
                          value={level.value}
                          checked={formData.permissionLevel === level.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${level.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{level.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{level.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Davet Mesajı (Opsiyonel)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Bu takvimi sizinle paylaşmak istiyorum..."
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSending || !formData.userId}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isSending || !formData.userId
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-purple-600 hover:bg-purple-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-purple-600/25'
                }`}
            >
              <Send className={`w-4 h-4 ${isSending ? 'animate-pulse' : ''}`} />
              <span>{isSending ? 'Gönderiliyor...' : 'Davet Gönder'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShareCalendarModal;